import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from 'axios';

// 同一張圖（同 api + file_id）在整個頁面生命週期只下載一次。
// 沒有快取時，像新書展示輪播這種會反覆掛載同一批封面的畫面，
// 每輪都會把整批圖重抓一次，瀏覽器的連線數被塞滿，封面就遲遲不顯示。
const MAX_CACHE_ENTRIES = 300;
const blobCache = new Map();    // cacheKey -> Blob
const pendingCache = new Map(); // cacheKey -> Promise<Blob>

const getCacheKey = (api, file_id) => `${api}?file_id=${file_id}`;

const trimCache = () => {
    while (blobCache.size > MAX_CACHE_ENTRIES) {
        const oldestKey = blobCache.keys().next().value;
        blobCache.delete(oldestKey);
    }
};

// 同一個 file_id 同時被多個元件要求時，只發一次請求
const fetchFileBlob = (api, file_id) => {
    const cacheKey = getCacheKey(api, file_id);
    if (blobCache.has(cacheKey)) {
        return Promise.resolve(blobCache.get(cacheKey));
    }
    if (pendingCache.has(cacheKey)) {
        return pendingCache.get(cacheKey);
    }
    const request = axios
        .get(api, {
            responseType: "blob",
            params: { file_id },
        })
        .then((response) => {
            blobCache.set(cacheKey, response.data);
            trimCache();
            return response.data;
        })
        .finally(() => {
            pendingCache.delete(cacheKey);
        });
    pendingCache.set(cacheKey, request);
    return request;
};

// 檔案內容被就地覆蓋（例如書封裁切後走 /replace，file_id 不變）時要呼叫，
// 否則畫面會沿用快取裡的舊圖。不帶參數則清空整個快取。
const invalidateImageFileCache = (file_id, api = "/component/file") => {
    if (file_id === null || file_id === undefined) {
        blobCache.clear();
        pendingCache.clear();
        return;
    }
    const fileIds = Array.isArray(file_id) ? file_id : [file_id];
    fileIds.forEach((id) => {
        const cacheKey = getCacheKey(api, id);
        blobCache.delete(cacheKey);
        pendingCache.delete(cacheKey);
    });
};

const ImageFileId = ({ src, alt, width, className = "", file_id, api = "/component/file", customize = false }) => { //不要ｍｅｒｇｅ，圖書自動化還是用舊的route
    const [blobData, setBlobData] = useState(
        () => (file_id ? blobCache.get(getCacheKey(api, file_id)) ?? null : null)
    );
    const [fileSrc, setFileSrc] = useState(src ?? null);

    // 處理 src 傳入時直接更新 fileSrc
    useEffect(() => {
        if (src) {
            setFileSrc(src);
        }
    }, [src]);

    // 透過 file_id 下載 blob
    useEffect(() => {
        let isCancelled = false;
        if (!file_id) {
            return undefined;
        }

        // 已經抓過就直接用，避免換圖時閃一下 placeholder
        const cached = blobCache.get(getCacheKey(api, file_id));
        if (cached) {
            setBlobData(cached);
            return undefined;
        }

        setBlobData(null);
        fetchFileBlob(api, file_id)
            .then((blob) => {
                if (!isCancelled) {
                    setBlobData(blob);
                }
            })
            .catch(() => { });

        return () => {
            isCancelled = true;
        };
    }, [file_id, api]);

    // 使用 useMemo 建立 ObjectURL，只有當 blobData 改變時才重新計算
    const objectUrl = useMemo(() => {
        if (!blobData) return null;
        const blob = new Blob([blobData], { type: "image/jpg" });
        return URL.createObjectURL(blob);
    }, [blobData]);

    // 設定 fileSrc 並在 unmount 時釋放 URL
    useEffect(() => {
        if (objectUrl) {
            setFileSrc(objectUrl);
            return () => {
                URL.revokeObjectURL(objectUrl);
            };
        }
        // 換成另一張還沒下載回來的圖：舊的 ObjectURL 已被釋放，不能繼續指著它
        if (file_id && !src) {
            setFileSrc(null);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUrl]);

    return (
        <ResponsiveNativeImage
            customize={customize}
            src={fileSrc}
            alt={alt}
            width={width}
            className={className}
        />
    );
};

const ResponsiveNativeImage = ({ src, alt, width, className = "", customize = false }) => {
    const [height, setHeight] = useState(undefined);
    const imgRef = useRef(null);

    useEffect(() => {
        const updateHeight = () => {
            const img = imgRef.current;
            if (img && img.naturalWidth && img.naturalHeight) {
                const aspectRatio = img.naturalWidth / img.naturalHeight;
                setHeight(Math.round(width / aspectRatio));
            }
        };

        if (!src) {
            // 沒有 src 時不要 preload，否則會對頁面網址（src="null"）多發一次請求
            return undefined;
        }

        const preloadImg = new Image();
        preloadImg.onload = updateHeight;
        preloadImg.src = src;

        if (imgRef.current?.complete) {
            updateHeight();
        }

        return () => {
            preloadImg.onload = null;
        };
    }, [src, width]);

    return (
        customize ?
            <img
                ref={imgRef}
                src={src || "/placeholder.svg"}
                alt={alt}
                className={` ${className}`}
                onLoad={() => {
                    const img = imgRef.current;
                    if (img && img.naturalWidth && img.naturalHeight) {
                        const aspectRatio = img.naturalWidth / img.naturalHeight;
                        setHeight(Math.round(width / aspectRatio));
                    }
                }}
            />
            :
            <img
                ref={imgRef}
                src={src || "/placeholder.svg"}
                alt={alt}
                width={width}
                height={height}
                className={`max-w-full ${className}`}
                style={{
                    width: `${width}px`,
                    height: height ? `${height}px` : "auto",
                    objectFit: "contain",
                }}
                onLoad={() => {
                    const img = imgRef.current;
                    if (img && img.naturalWidth && img.naturalHeight) {
                        const aspectRatio = img.naturalWidth / img.naturalHeight;
                        setHeight(Math.round(width / aspectRatio));
                    }
                }}
            />
    );
};

export { invalidateImageFileCache };
export default ImageFileId;
